import { useEffect, useRef, useCallback, useState } from 'react';
import { 
  getOrCreateAttentionMetrics, 
  updateAttentionMetrics, 
  trackClick as trackClickService,
  trackKaiQuery,
  trackTabVisit,
  trackZoneVisibility,
  type AttentionData 
} from '@/services/analytics/attentionService';

// Get or create session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('attention_session_id');
  if (!sessionId) {
    sessionId = `attn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('attention_session_id', sessionId);
  }
  return sessionId;
};

// Get device info
const getDeviceInfo = () => ({
  userAgent: navigator.userAgent,
  language: navigator.language,
  platform: navigator.platform,
  screenResolution: `${screen.width}x${screen.height}`,
  windowSize: `${window.innerWidth}x${window.innerHeight}`,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  touchSupport: 'ontouchstart' in window,
});

interface UseAttentionTrackerOptions {
  interviewId: string;
  userId?: string;
  enabled?: boolean;
}

interface ZoneData {
  startTime: number | null;
  totalSeconds: number;
  isVisible: boolean;
}

export interface AttentionTrackerAPI {
  trackClick: (clickType: 'expand' | 'question' | 'person' | 'company' | 'tag' | 'context') => void;
  trackKaiMessage: (query: string, category?: string) => void;
  trackTabSwitch: (tabName: string) => void;
  trackQuizComplete: (score: number) => void;
  trackPollAnswer: () => void;
  trackDebateAction: (action: 'argument' | 'vote') => void;
  registerZone: (zoneId: string, element: HTMLElement | null) => void;
  unregisterZone: (zoneId: string) => void;
}

const SYNC_INTERVAL_MS = 10000; // Sync every 10 seconds
const INACTIVITY_TIMEOUT_MS = 5000; // Stop counting after 5 seconds of inactivity

export function useAttentionTracker({
  interviewId,
  userId,
  enabled = true,
}: UseAttentionTrackerOptions): AttentionTrackerAPI {
  const sessionId = useRef(getSessionId());
  const metricsId = useRef<string | null>(null);
  const isInitialized = useRef(false);
  
  // Accumulated data since last sync
  const accumulatedData = useRef<Partial<AttentionData>>({
    passiveSeconds: 0,
    scrollDepthPercent: 0,
    kaiSeconds: 0,
    kaiMessages: 0,
    quizSeconds: 0,
    debateSeconds: 0,
  });

  // Activity tracking
  const lastActivityTime = useRef(Date.now());
  const isUserActive = useRef(true);
  const isTabVisible = useRef(!document.hidden);
  const passiveStartTime = useRef<number | null>(null);

  // Kai/Quiz/Debate time tracking
  const kaiStartTime = useRef<number | null>(null);
  const quizStartTime = useRef<number | null>(null);
  const debateStartTime = useRef<number | null>(null);

  // Zone tracking
  const zones = useRef<Map<string, ZoneData>>(new Map());
  const observers = useRef<Map<string, IntersectionObserver>>(new Map());

  // Current tab for time tracking
  const [currentTab, setCurrentTab] = useState<string | null>(null);

  // Initialize metrics record
  useEffect(() => {
    if (!enabled || !interviewId || isInitialized.current) return;

    const init = async () => {
      const id = await getOrCreateAttentionMetrics(
        sessionId.current,
        interviewId,
        userId,
        getDeviceInfo()
      );
      metricsId.current = id;
      isInitialized.current = true;
      passiveStartTime.current = Date.now();
    };

    init();
  }, [interviewId, userId, enabled]);

  // Activity detection
  useEffect(() => {
    if (!enabled) return;

    const handleActivity = () => {
      lastActivityTime.current = Date.now();
      if (!isUserActive.current) {
        isUserActive.current = true;
        // Resume passive time tracking
        if (!passiveStartTime.current && isTabVisible.current) {
          passiveStartTime.current = Date.now();
        }
      }
    };

    const events = ['scroll', 'mousemove', 'mousedown', 'keydown', 'touchstart', 'touchmove'];
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Check for inactivity
    const inactivityInterval = setInterval(() => {
      const now = Date.now();
      if (now - lastActivityTime.current > INACTIVITY_TIMEOUT_MS && isUserActive.current) {
        isUserActive.current = false;
        // Pause passive time tracking and accumulate
        if (passiveStartTime.current) {
          const elapsed = Math.floor((now - passiveStartTime.current) / 1000);
          accumulatedData.current.passiveSeconds = (accumulatedData.current.passiveSeconds || 0) + elapsed;
          passiveStartTime.current = null;
        }
      }
    }, 1000);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearInterval(inactivityInterval);
    };
  }, [enabled]);

  // Tab visibility tracking
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      isTabVisible.current = !document.hidden;
      
      if (document.hidden) {
        // Pause tracking
        if (passiveStartTime.current) {
          const elapsed = Math.floor((Date.now() - passiveStartTime.current) / 1000);
          accumulatedData.current.passiveSeconds = (accumulatedData.current.passiveSeconds || 0) + elapsed;
          passiveStartTime.current = null;
        }
      } else if (isUserActive.current) {
        // Resume tracking
        passiveStartTime.current = Date.now();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enabled]);

  // Scroll depth tracking
  useEffect(() => {
    if (!enabled) return;

    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight > 0) {
        const scrollPercent = Math.min(100, Math.round((window.scrollY / scrollHeight) * 100));
        accumulatedData.current.scrollDepthPercent = Math.max(
          accumulatedData.current.scrollDepthPercent || 0,
          scrollPercent
        );
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [enabled]);

  // Sync data to database periodically
  useEffect(() => {
    if (!enabled || !interviewId) return;

    const syncData = async () => {
      // Calculate current passive time if still tracking
      let currentPassive = accumulatedData.current.passiveSeconds || 0;
      if (passiveStartTime.current && isUserActive.current && isTabVisible.current) {
        const elapsed = Math.floor((Date.now() - passiveStartTime.current) / 1000);
        currentPassive += elapsed;
        passiveStartTime.current = Date.now(); // Reset start time
      }

      // Calculate zone visibility
      const zoneData: Record<string, number> = {};
      zones.current.forEach((data, zoneId) => {
        let seconds = data.totalSeconds;
        if (data.isVisible && data.startTime && isUserActive.current && isTabVisible.current) {
          seconds += Math.floor((Date.now() - data.startTime) / 1000);
          data.startTime = Date.now(); // Reset
        }
        if (seconds > 0) {
          zoneData[zoneId] = seconds;
        }
        data.totalSeconds = 0; // Reset after sync
      });

      const dataToSync: Partial<AttentionData> = {
        passiveSeconds: currentPassive,
        scrollDepthPercent: accumulatedData.current.scrollDepthPercent || 0,
        kaiSeconds: accumulatedData.current.kaiSeconds || 0,
        kaiMessages: accumulatedData.current.kaiMessages || 0,
        quizSeconds: accumulatedData.current.quizSeconds || 0,
        debateSeconds: accumulatedData.current.debateSeconds || 0,
        zoneVisibility: Object.keys(zoneData).length > 0 ? zoneData : undefined,
      };

      // Only sync if there's data
      const hasData = Object.values(dataToSync).some(v => 
        v !== undefined && v !== 0 && (typeof v !== 'object' || Object.keys(v).length > 0)
      );

      if (hasData) {
        await updateAttentionMetrics(sessionId.current, interviewId, dataToSync);
        
        // Reset accumulated data
        accumulatedData.current = {
          passiveSeconds: 0,
          scrollDepthPercent: accumulatedData.current.scrollDepthPercent, // Keep max scroll
          kaiSeconds: 0,
          kaiMessages: 0,
          quizSeconds: 0,
          debateSeconds: 0,
        };
      }
    };

    const syncInterval = setInterval(syncData, SYNC_INTERVAL_MS);

    // Sync on unmount/page leave
    const handleBeforeUnload = () => {
      syncData();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(syncInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      syncData(); // Final sync
    };
  }, [interviewId, enabled]);

  // Click tracking
  const trackClick = useCallback((clickType: 'expand' | 'question' | 'person' | 'company' | 'tag' | 'context') => {
    if (!enabled || !interviewId) return;
    trackClickService(sessionId.current, interviewId, clickType);
  }, [interviewId, enabled]);

  // Kai message tracking
  const trackKaiMessage = useCallback((query: string, category?: string) => {
    if (!enabled || !interviewId) return;
    
    accumulatedData.current.kaiMessages = (accumulatedData.current.kaiMessages || 0) + 1;
    trackKaiQuery(interviewId, query, category);

    // Track Kai time
    if (kaiStartTime.current) {
      const elapsed = Math.floor((Date.now() - kaiStartTime.current) / 1000);
      accumulatedData.current.kaiSeconds = (accumulatedData.current.kaiSeconds || 0) + elapsed;
    }
    kaiStartTime.current = Date.now();
  }, [interviewId, enabled]);

  // Tab switch tracking
  const trackTabSwitch = useCallback((tabName: string) => {
    if (!enabled || !interviewId) return;

    // Stop previous tab timer
    if (currentTab) {
      if (currentTab === 'kai' && kaiStartTime.current) {
        const elapsed = Math.floor((Date.now() - kaiStartTime.current) / 1000);
        accumulatedData.current.kaiSeconds = (accumulatedData.current.kaiSeconds || 0) + elapsed;
        kaiStartTime.current = null;
      } else if (currentTab === 'quiz' && quizStartTime.current) {
        const elapsed = Math.floor((Date.now() - quizStartTime.current) / 1000);
        accumulatedData.current.quizSeconds = (accumulatedData.current.quizSeconds || 0) + elapsed;
        quizStartTime.current = null;
      } else if (currentTab === 'debate' && debateStartTime.current) {
        const elapsed = Math.floor((Date.now() - debateStartTime.current) / 1000);
        accumulatedData.current.debateSeconds = (accumulatedData.current.debateSeconds || 0) + elapsed;
        debateStartTime.current = null;
      }
    }

    // Start new tab timer
    const now = Date.now();
    if (tabName === 'kai') {
      kaiStartTime.current = now;
    } else if (tabName === 'quiz') {
      quizStartTime.current = now;
    } else if (tabName === 'debate') {
      debateStartTime.current = now;
    }

    setCurrentTab(tabName);
    trackTabVisit(sessionId.current, interviewId, tabName);
  }, [interviewId, currentTab, enabled]);

  // Quiz completion tracking
  const trackQuizComplete = useCallback((score: number) => {
    if (!enabled || !interviewId) return;
    
    updateAttentionMetrics(sessionId.current, interviewId, {
      quizCompleted: true,
      quizScore: score,
    });
  }, [interviewId, enabled]);

  // Poll answer tracking
  const trackPollAnswer = useCallback(() => {
    if (!enabled || !interviewId) return;
    
    updateAttentionMetrics(sessionId.current, interviewId, {
      pollAnswered: true,
    });
  }, [interviewId, enabled]);

  // Debate action tracking
  const trackDebateAction = useCallback((action: 'argument' | 'vote') => {
    if (!enabled || !interviewId) return;
    
    updateAttentionMetrics(sessionId.current, interviewId, {
      debateArguments: action === 'argument' ? 1 : 0,
      debateVotes: action === 'vote' ? 1 : 0,
    });
  }, [interviewId, enabled]);

  // Zone registration for visibility tracking
  const registerZone = useCallback((zoneId: string, element: HTMLElement | null) => {
    if (!enabled || !element) return;

    // Clean up existing observer
    const existingObserver = observers.current.get(zoneId);
    if (existingObserver) {
      existingObserver.disconnect();
    }

    // Initialize zone data
    zones.current.set(zoneId, {
      startTime: null,
      totalSeconds: 0,
      isVisible: false,
    });

    // Create intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const zoneData = zones.current.get(zoneId);
          if (!zoneData) return;

          if (entry.isIntersecting && isUserActive.current && isTabVisible.current) {
            if (!zoneData.isVisible) {
              zoneData.isVisible = true;
              zoneData.startTime = Date.now();
            }
          } else {
            if (zoneData.isVisible && zoneData.startTime) {
              const elapsed = Math.floor((Date.now() - zoneData.startTime) / 1000);
              zoneData.totalSeconds += elapsed;
              zoneData.startTime = null;
            }
            zoneData.isVisible = false;
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(element);
    observers.current.set(zoneId, observer);
  }, [enabled]);

  // Zone unregistration
  const unregisterZone = useCallback((zoneId: string) => {
    const observer = observers.current.get(zoneId);
    if (observer) {
      observer.disconnect();
      observers.current.delete(zoneId);
    }

    // Save remaining time before removing
    const zoneData = zones.current.get(zoneId);
    if (zoneData && zoneData.isVisible && zoneData.startTime && interviewId) {
      const elapsed = Math.floor((Date.now() - zoneData.startTime) / 1000);
      trackZoneVisibility(sessionId.current, interviewId, zoneId, zoneData.totalSeconds + elapsed);
    }
    zones.current.delete(zoneId);
  }, [interviewId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      observers.current.forEach((observer) => observer.disconnect());
      observers.current.clear();
      zones.current.clear();
    };
  }, []);

  return {
    trackClick,
    trackKaiMessage,
    trackTabSwitch,
    trackQuizComplete,
    trackPollAnswer,
    trackDebateAction,
    registerZone,
    unregisterZone,
  };
}
